// AdminRiskFlagsPage — Real-time risk flag dashboard
// Flags: low days, consistency breach, high margin, suspicious patterns

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Flag {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'info';
  category: 'low-days' | 'consistency' | 'margin' | 'pattern' | 'velocity' | 'region';
  trader: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  detail: string;
  metric: string;
  triggered: string;
  status: 'open' | 'reviewing' | 'resolved' | 'dismissed';
}

const FLAGS: Flag[] = [
  { id: 'f1',  severity: 'critical', category: 'margin',      trader: 'Devon K.', avatar: 4, account: 'FP-100K-13892', tier: 'Mid',          detail: 'Single position using 32% of margin (limit 30%)', metric: '32% / 30%',  triggered: '5m ago',  status: 'open' },
  { id: 'f2',  severity: 'high',     category: 'consistency', trader: 'Sarah C.', avatar: 2, account: 'FP-150K-12844', tier: 'Professional', detail: 'Day profit 86% of target (eval limit 40%)',     metric: '86% / 40%',  triggered: '12m ago', status: 'open' },
  { id: 'f3',  severity: 'high',     category: 'velocity',    trader: 'Felix W.', avatar: 1, account: 'FP-50K-14672',  tier: 'Beginner',     detail: '38 trades in 12 min — potential HFT',           metric: '38/12min',   triggered: '25m ago', status: 'reviewing' },
  { id: 'f4',  severity: 'medium',   category: 'low-days',    trader: 'Riley T.', avatar: 3, account: 'FP-50K-15201',  tier: 'Beginner',     detail: 'Only 1 trading day in 14 days',                 metric: '1/14 days',  triggered: '1h ago',  status: 'open' },
  { id: 'f5',  severity: 'medium',   category: 'pattern',     trader: 'Brent O.', avatar: 3, account: 'FP-100K-14821', tier: 'Mid',          detail: 'Same entry price 6x in a row',                  metric: '6x same',    triggered: '2h ago',  status: 'open' },
  { id: 'f6',  severity: 'high',     category: 'region',      trader: 'Ana M.',   avatar: 2, account: 'FP-150K-13256', tier: 'Professional', detail: 'Login from 3 different countries in 24h',      metric: '3 regions',  triggered: '3h ago',  status: 'reviewing' },
  { id: 'f7',  severity: 'info',     category: 'low-days',    trader: 'Lisa H.',  avatar: 4, account: 'FP-50K-15034',  tier: 'Beginner',     detail: 'Trading 4 days/week consistently',              metric: '4/7 days',   triggered: '6h ago',  status: 'dismissed' },
  { id: 'f8',  severity: 'critical', category: 'margin',      trader: 'Alex P.',  avatar: 1, account: 'FP-150K-14201', tier: 'Professional', detail: 'Open loss > 4% max DD',                         metric: '4.2% / 4%',  triggered: '15m ago', status: 'open' },
];

const SEV_META: Record<string, { color: string; bg: string; label: string; icon: string }> = {
  critical: { color: '#FF3B30', bg: 'rgba(255,59,48,0.15)',  label: 'Critical', icon: '🚨' },
  high:     { color: '#FF9500', bg: 'rgba(255,149,0,0.15)',  label: 'High',     icon: '⚠️' },
  medium:   { color: '#FFD60A', bg: 'rgba(255,214,10,0.15)', label: 'Medium',   icon: '⚠' },
  info:     { color: '#0A84FF', bg: 'rgba(10,132,255,0.15)', label: 'Info',     icon: 'ℹ️' },
};

const CAT_META: Record<string, { label: string; icon: string }> = {
  'low-days':   { label: 'Low trading days',  icon: '📅' },
  'consistency':{ label: 'Consistency rule',  icon: '📊' },
  'margin':     { label: 'Margin limit',      icon: '🛡️' },
  'pattern':    { label: 'Trading pattern',   icon: '🔍' },
  'velocity':   { label: 'Trade velocity',    icon: '⚡' },
  'region':     { label: 'Region anomaly',    icon: '🌍' },
};

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };

export function AdminRiskFlagsPage() {
  const nav = useNavigate();
  const [flags, setFlags] = useState(FLAGS);
  const [filter, setFilter] = useState<'all' | 'critical' | 'high' | 'open' | 'reviewing'>('open');

  const filtered = flags.filter((f) => {
    if (filter === 'all') return true;
    if (filter === 'open' || filter === 'reviewing') return f.status === filter;
    return f.severity === filter;
  });

  function setStatus(id: string, status: Flag['status']) {
    setFlags((curr) => curr.map((f) => (f.id === id ? { ...f, status } : f)));
  }

  const counts = {
    open: flags.filter((f) => f.status === 'open').length,
    reviewing: flags.filter((f) => f.status === 'reviewing').length,
    critical: flags.filter((f) => f.severity === 'critical').length,
    high: flags.filter((f) => f.severity === 'high').length,
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Risk flags</div>
        <div className="w-9" />
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-4 gap-2">
        <Stat label="Open"      value={counts.open}      color="#FF3B30" />
        <Stat label="Reviewing" value={counts.reviewing} color="#FF9500" />
        <Stat label="Critical"  value={counts.critical}  color="#FF3B30" />
        <Stat label="High"      value={counts.high}      color="#FF9500" />
      </div>

      {/* Filter */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['open', 'reviewing', 'critical', 'high', 'all'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`ffilter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{f}</button>
        ))}
      </div>

      {/* Flags list */}
      <div className="px-4 mt-3 space-y-2">
        {filtered.map((f) => {
          const sm = SEV_META[f.severity];
          const cm = CAT_META[f.category];
          return (
            <div key={f.id} data-testid={`flag-${f.id}`} className={['rounded-[14px] border p-3',
              f.status === 'open' && f.severity === 'critical' ? 'border-[#FF3B30]/50 bg-[#FF3B30]/5' :
              f.status === 'resolved' || f.status === 'dismissed' ? 'border-white/5 bg-white/5 opacity-60' :
              'border-white/10 bg-white/5'
            ].join(' ')}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[20px]" style={{ backgroundColor: sm.bg }}>{cm.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: sm.bg, color: sm.color }}>{sm.icon} {sm.label}</span>
                    <span className="text-[10px] text-white/40">·</span>
                    <span className="text-[10px] text-white/60 uppercase tracking-wider">{cm.label}</span>
                  </div>
                  <div className="mt-0.5 text-[13px] font-semibold text-white">{f.trader} · {f.account}</div>
                  <p className="text-[11px] text-white/60 mt-0.5">{f.detail}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-mono text-white/80">{f.metric}</span>
                    <span className="text-[10px] text-white/40">{f.triggered}</span>
                  </div>
                </div>
              </div>
              {(f.status === 'open' || f.status === 'reviewing') && (
                <div className="mt-2 flex gap-2">
                  {f.status === 'open' && <button onClick={() => setStatus(f.id, 'reviewing')} data-testid={`review-${f.id}`} className="flex-1 h-8 rounded-full bg-[#FF9500] text-black text-[12px] font-bold active:opacity-40">Review</button>}
                  <button onClick={() => setStatus(f.id, 'resolved')} data-testid={`resolve-${f.id}`} className="flex-1 h-8 rounded-full bg-[#34C759] text-black text-[12px] font-bold active:opacity-40">Resolve</button>
                  <button onClick={() => setStatus(f.id, 'dismissed')} data-testid={`dismiss-${f.id}`} className="flex-1 h-8 rounded-full bg-white/10 text-white text-[12px] font-semibold active:opacity-40">Dismiss</button>
                </div>
              )}
              {f.status === 'resolved' && <div className="mt-2 text-[10px] text-[#34C759]">✓ Resolved</div>}
              {f.status === 'dismissed' && <div className="mt-2 text-[10px] text-white/40">⊘ Dismissed</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
      <div className="text-[9px] text-white/60 uppercase">{label}</div>
      <div className="text-[16px] font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
